<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    @mousedown="focused = true"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
    @click="handleClick"
  >
    <Icon
      v-if="loading && (!iconDirection || iconDirection === 'left')"
      icon="eos-icons:bubble-loading"
      :width="iconSizeClasses"
    ></Icon>
    <Icon
      v-if="icon && !loading && iconDirection === 'left'"
      :class="icon.class"
      :icon="icon.icon"
      :color="icon.color ?? iconColorClasses"
      :width="icon.width ?? iconSizeClasses"
    />
    <img v-if="image && !loading && iconDirection === 'left'" :src="image" />

    <p v-if="label && !loading">
      {{ label }}
    </p>

    <Icon
      v-if="loading && iconDirection === 'right'"
      icon="eos-icons:bubble-loading"
      :width="iconSizeClasses"
    ></Icon>
    <Icon
      v-if="icon && !loading && iconDirection === 'right'"
      :class="icon.class"
      :icon="icon.icon"
      :color="icon.color ?? iconColorClasses"
      :width="icon.width ?? iconSizeClasses"
    />
    <img v-if="image && !loading && iconDirection === 'right'" :src="image" />
  </button>
</template>

<script setup lang="ts">
import Icon from '@/shared/ui/components/general/Icon.vue'
import { ref, computed } from 'vue'

interface Props {
  label?: string
  textColor?: string
  size: 'lg' | 'md'
  bold: 'semibold' | 'regular'
  subTitle?: boolean
  icon?: Icon
  image?: string
  iconDirection?: 'left' | 'right'
  disabled?: boolean
  loading?: boolean
  active?: boolean
  dark?: boolean
}
const props = defineProps<Props>()
const emit = defineEmits(['click'])

const hovered = ref(false)
const focused = ref(false)

const iconColorClasses = computed(() => {
  const typeMap: Record<'default' | 'active' | 'disabled', string> = {
    default: props.dark ? '#FFFFFF' : '#161817',
    active: '#00B14F',
    disabled: '#E0E5E3'
  }
  return typeMap[props.disabled ? 'disabled' : props.active ? 'active' : 'default']
})

const iconSizeClasses = computed(() => {
  const sizeMap: Record<'lg' | 'md', string> = {
    lg: '20',
    md: '16'
  }
  return sizeMap[props.size]
})

const buttonClasses = computed(() => {
  const base = 'flex items-center justify-center transition-2'

  const sizeMap: Record<string, Record<string, string>> = {
    lg: { regular: 'h-11 text-small gap-2', semibold: 'h-11 text-small-semibold gap-2' },
    md: { regular: 'h-9 text-xsmall gap-2', semibold: 'h-9 text-xsmall-semibold gap-2' }
  }

  const statusMap: Record<string, Record<string, string>> = {
    default: {
      regular: 'border-b border-primary-50/0',
      semibold: 'border-b border-primary-50/0'
    },
    hover: {
      regular: 'border-b border-primary-50',
      semibold: 'border-b border-primary-50'
    },
    active: {
      regular: 'border-b border-primary-50/0',
      semibold: 'border-b border-primary-50/0'
    },
    disabled: {
      regular: 'cursor-not-allowed',
      semibold: 'cursor-not-allowed'
    }
  }

  const textColorMap: Record<string, Record<string, string>> = {
    default: {
      default: props.textColor ? props.textColor : props.dark ? 'text-white' : 'text-gray-95',
      hover: props.textColor ? props.textColor : props.dark ? 'text-white' : 'text-gray-95',
      active: props.dark ? 'text-primary-40' : 'text-primary-50',
      disabled: props.dark ? 'text-gray-60' : 'text-gray-20'
    },
    sub: {
      default: props.dark ? 'text-gray-60' : 'text-gray-20',
      hover: props.dark ? 'text-gray-60' : 'text-gray-20',
      active: props.dark ? 'text-gray-30' : 'text-gray-70',
      disabled: props.dark ? 'text-gray-60' : 'text-gray-20'
    }
  }

  const state = focused.value || props.active ? 'active' : hovered.value ? 'hover' : 'default'

  return [
    base,
    sizeMap[props.size][props.bold],
    statusMap[state][props.bold],
    textColorMap[props.subTitle ? 'sub' : 'default'][state]
  ].join(' ')
})

const handleClick = () => {
  focused.value = false
  if (!props.disabled) emit('click')
}
</script>

<style scoped>
button:disabled {
  color: #58616a;
  background-color: #33363d;
  cursor: not-allowed;
}
</style>
